import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  Avatar,
  ConfirmDialog,
  Drawer,
  IconButton,
} from "@outpave/ds";
import { Badge, Button, useMediaQuery } from "@mui/material";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useInvoiceData } from "../../../hooks/useInvoiceData";
import CommentsPanel from "components/common/comments/CommentsPanel";
import { useComments } from "hooks/useComments";
import { ParentType } from "../../../enums/Comments";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  BubbleChatIcon,
  ImageAdd01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { InvoiceStatus } from "enums/InvoiceStatus";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import InvoicePreviewContainer from "./InvoicePreviewContainer";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import useGetInvoiceStatusDetails from "hooks/useGetInvoiceStatusDetails";
import Summary from "../../common/Summary";
import RenderInvoiceStatus from "components/common/RenderInvoiceStatus";
import { formatCashFull } from "utils/formatCash";
import { contactName } from "utils/fullName";
import { Typography } from "@mui/material";
import { ContactRouterDrawerType } from "enums/ContactRouterDrawerType";
import ContactDrawerRouter from "../contacts/ContactDrawerRouter";
import IdNotFoundPage from "components/common/IdNotFoundPage";
import {
  EditInvoiceConfirmationModal,
  RefundInvoiceConfirmationModal,
  ResendFailedPaymentNoticeModal,
  SendInvoiceAsEstimateConfirmationModal,
  SendInvoiceConfirmationModal,
} from "./InvoiceModules";
import {
  buildInvoiceHistoryRows,
  buildInvoiceSummaryItems,
  InvoiceHistoryTable,
  InvoiceTotalDetails,
  LineItemsTable,
} from "./SharedInvoiceComponents";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { useFeatureEntitlements } from "@frontegg/react";
import FormikCustomFields from "components/common/FormikCustomFields";
import TransactionStatus from "enums/TransactionStatus";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import SectionTabs, { SectionTab } from "components/common/SectionTabs";
import PanelSection from "components/common/PanelSection";
import SectionGlyph from "components/common/SectionGlyph";

const DRAWER_WIDTH = 544;

interface AttachedFilesDrawerProps {
  open: boolean;
  invoiceId: string;
  handleCloseInvoiceDetailsDrawer: () => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const InvoiceDetailsDrawer = (props: AttachedFilesDrawerProps) => {
  const { open, invoiceId, handleCloseInvoiceDetailsDrawer, onAfterClose } =
    props;
  const { t } = useTranslation();

  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });

  const [transactionReviewDrawerId, setTransactionReviewDrawerId] = useState<
    string | null
  >(null);
  const [openInvoicePreview, setOpenInvoicePreview] = useState(false);
  const [openEditSentInvoiceModal, setOpenEditSentInvoiceModal] =
    useState(false);
  const [openRefundInvoiceModal, setOpenRefundInvoiceModal] = useState(false);
  const [openContactDetailsDrawer, setOpenContactDetailsDrawer] = useState<
    string | null
  >(null);
  const [
    openSendInvoiceConfirmationModal,
    setOpenSendInvoiceConfirmationModal,
  ] = useState(false);
  const [openSendInvoiceAsEstimateModal, setOpenSendInvoiceAsEstimateModal] =
    useState(false);
  const [openResendFailedNoticeModal, setOpenResendFailedNoticeModal] =
    useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [openUnsavedChangesModal, setOpenUnsavedChangesModal] = useState(false);
  // The panel docks beside the drawer only when there is room for both.
  const commentsDockable = useMediaQuery(`(min-width:${DRAWER_WIDTH + 520}px)`);

  const {
    invoiceData,
    formikInvoice,
    customFieldSettings,
    specificLoadingStates,
    handleOpenEditInvoicePage,
    handleSaveInvoice,
    refetchInvoice,
  } = useInvoiceData({
    invoiceId,
    shouldFetchAttachments: false,
  });

  const handleOpenInvoicePreview = () => {
    setOpenInvoicePreview(true);
  };

  const handleCloseViewContactDetailsDrawer = () => {
    setOpenContactDetailsDrawer(null);
  };

  // The saved values, not the live ones: the Summary reports what the invoice
  // says, and `values` is what the editor in the Details tab is writing, so
  // feeding it here mirrored every keystroke into the summary above.
  //
  // `hasVisibleCustomFields` reads these too and is unaffected: the only
  // value-sensitive branch in `isCustomFieldVisible` is READONLY, whose live
  // and saved values are the same thing.
  const { filteredCustomFieldSummaryItems, hasVisibleCustomFields } =
    useCustomFieldRenderer({
      parentType: CustomFieldParentType.INVOICE,
      customFields: formikInvoice.initialValues.customFields,
    });

  const invoice = {
    ...formikInvoice.values,
    ...(featureCustomFields ? { filteredCustomFieldSummaryItems } : {}),
    paymentError: invoiceData?.paymentError,
    microdepositPending: invoiceData?.microdepositPending,
  };

  const { isInvoiceEditable, isInvoiceSendable, isInvoiceSent, isEstimate } =
    useGetInvoiceStatusDetails(invoice.status as InvoiceStatus);

  const isLoading = specificLoadingStates.populatingInvoice && !invoice.id;

  // The panel sits above the drawer layer but below the modal one, so it has to
  // stand down for the two drawers this one opens rather than cover them.
  const showComments =
    open &&
    commentsOpen &&
    !openContactDetailsDrawer &&
    !transactionReviewDrawerId;

  const commentActions = useComments(invoiceId, ParentType.INVOICE);
  const commentCount = commentActions.comments?.length ?? 0;

  const isShowRefundButton = useMemo(() => {
    return (
      !isAuditor &&
      ((invoiceData?.status === InvoiceStatus.COMPLETE &&
        invoiceData?.transaction?.status === TransactionStatus.COMPLETE) ||
        invoiceData?.transaction?.status ===
          TransactionStatus.PARTIALLY_REFUNDED)
    );
  }, [invoiceData, isAuditor]);

  useEffect(() => {
    if (invoice?.status === InvoiceStatus.ESTIMATE_REJECTED) {
      setCommentsOpen(true);
    }
  }, [invoice?.status]);

  const summaryItems = buildInvoiceSummaryItems({
    invoice,
    setOpenContactDetailsDrawer,
    setTransactionReviewDrawerId,
  });

  // Custom fields are the only formik-bound editor in this drawer, so a dirty
  // form means unsaved custom fields and nothing else. Attachments upload
  // against their own parent and never reach these values.
  const hasUnsavedChanges =
    formikInvoice.dirty &&
    !!formikInvoice.values.id &&
    !specificLoadingStates.populatingInvoice;

  // Both ways out land here: the footer button, and the drawer's own close
  // and Escape. Discarding resets the form rather than just closing, because
  // the drawer stays mounted and reopening the same invoice does not repopulate
  // it, so abandoned edits would still be sitting there.
  const handleCloseWithCheck = (discard: boolean = false) => {
    if (hasUnsavedChanges && !discard) {
      setOpenUnsavedChangesModal(true);
      return;
    }
    if (discard) formikInvoice.resetForm();
    handleCloseInvoiceDetailsDrawer();
  };

  // Not memoized: `invoice` is rebuilt every render, and each hand-written
  // dependency list missed a field the builder reads and left History stale.
  const historyRows = buildInvoiceHistoryRows(invoice, t);

  const showCustomFieldsAccordion =
    featureCustomFields && hasVisibleCustomFields;

  // Three tabs, still not one per section. D14 reads a strip that has to
  // scroll as the wrong grouping rather than long labels, and the first cut
  // of this drawer took that to mean two: Details over everything the
  // invoice says, History over what happened to it. Line Items earns its own
  // tab because it is the only one carrying a table, and three labels fit the
  // 544px strip where the four it replaced did not. Attachments and Custom
  // Fields stay sections under Details rather than becoming tabs five and
  // six, which is the line D14 is actually drawing.
  const sectionTabs: SectionTab[] = [
    {
      key: "line-items",
      label: t("Line Items"),
      panel: (
        <Stack gap="var(--space-3)">
          <LineItemsTable
            details={invoice.details}
            currency={invoice.currency}
          />
          <InvoiceTotalDetails invoice={invoice} />
        </Stack>
      ),
    },
    {
      key: "details",
      label: t("Details"),
      panel: (
        <Stack gap="var(--space-6)">
          <PanelSection title={t("Attachments")}>
            <Accordion
              title={t("Attachments")}
              icon={
                <SectionGlyph aria-label={t("Attachments")}>
                  <HugeiconsIcon icon={ImageAdd01Icon} size={16} />
                </SectionGlyph>
              }
            >
              <AttachmentsComponent
                renderType={AttachmentRenderType.ACCORDION}
                drawerType={AttachmentDrawerType.ATTACHMENT}
                dragAndDropHint={t("JPG, PNG,\nWEBP, PDF\n(Max 10 MB)")}
                parentDetails={{
                  parentId: invoice.id || "",
                  parentType: ATTACHMENT_PARENT_TYPE.INVOICE,
                  subParentId: "invoice",
                }}
              />
            </Accordion>
          </PanelSection>

          {showCustomFieldsAccordion && (
            <PanelSection title={t("Custom Fields")}>
              <FormikCustomFields
                settings={customFieldSettings}
                formik={formikInvoice}
                variant="accordion"
              />
            </PanelSection>
          )}
        </Stack>
      ),
    },
    // An invoice with no history would otherwise render an empty tab.
    ...(historyRows.length > 0
      ? [
          {
            key: "history",
            label: t("History"),
            panel: <InvoiceHistoryTable rows={historyRows} />,
          },
        ]
      : []),
  ];

  const busy =
    isLoading ||
    specificLoadingStates.sendingInvoice ||
    (specificLoadingStates.updatingInvoice && !openEditSentInvoiceModal);

  return (
    <>
      <Drawer
        open={open}
        width={`${DRAWER_WIDTH}px`}
        onClose={() => handleCloseWithCheck()}
        onAfterClose={() => {
          setCommentsOpen(false);
          onAfterClose?.();
        }}
        title={t("Invoice Details")}
        loading={busy}
        disableEnforceFocus={showComments}
        headerAction={
          <IconButton
            variant="ghost"
            aria-label={t("Comments")}
            onClick={() => setCommentsOpen((wasOpen) => !wasOpen)}
          >
            <Badge badgeContent={commentCount} color="primary">
              <HugeiconsIcon icon={BubbleChatIcon} size={18} />
            </Badge>
          </IconButton>
        }
        footer={
          <>
            <Button
              variant="default-soft"
              onClick={() => handleCloseWithCheck()}
            >
              {t("Exit")}
            </Button>

            {/* Custom fields stay editable after the invoice itself stops
            being editable, so the save cannot sit behind the edit gate. */}
            {(hasUnsavedChanges || (isInvoiceEditable && !isAuditor)) && (
              <Button
                // Filled only with something to save; the send above is otherwise the one primary.
                variant={
                  hasUnsavedChanges ? "primary-filled" : "default-outlined"
                }
                onClick={async () => {
                  if (hasUnsavedChanges) {
                    const saved = await handleSaveInvoice(false, true);
                    // The custom-fields-only path returns before formik is
                    // reset, so the refetch is what clears dirty and puts
                    // the label back. A failed save skips it and keeps the edits.
                    if (saved) refetchInvoice();
                  } else if (isInvoiceSent) {
                    setOpenEditSentInvoiceModal(true);
                  } else if (invoice?.id) {
                    handleOpenEditInvoicePage(invoice.id);
                  }
                }}
                disabled={specificLoadingStates.updatingInvoice}
              >
                {hasUnsavedChanges ? t("Save Changes") : t("Edit Invoice")}
              </Button>
            )}
          </>
        }
      >
        {isLoading || !formikInvoice.values?.id ? (
          <IdNotFoundPage
            title={t("Invoice")}
            isLoading={isLoading}
            showSkeleton={true}
          />
        ) : (
          <>
            <Stack gap="var(--space-5)">
              <Inline
                justify="between"
                align="center"
                wrap={false}
                gap="var(--space-4)"
              >
                <Inline align="center" gap="var(--space-3)">
                  <Avatar
                    size="lg"
                    src={invoice?.primaryRecipient?.logo}
                    name={contactName(invoice?.primaryRecipient)}
                    alt={contactName(invoice?.primaryRecipient)}
                  />
                  <Typography variant="subtitle2">
                    {contactName(invoice?.primaryRecipient)}
                  </Typography>
                </Inline>
                <RenderInvoiceStatus
                  invoiceStatus={invoice?.status}
                  disputeStatus={invoice?.disputeData?.disputeStatus}
                  dueDate={invoice?.dueDate}
                  microdepositPending={invoice?.microdepositPending}
                />
              </Inline>

              <Typography variant="h2">
                {formatCashFull(invoice?.total, invoice?.currency)}
              </Typography>

              <Summary items={summaryItems} disableContainer />
            </Stack>

            {/* One filled action, the send, which gives way to Save Changes while edits are unsaved. */}
            <Inline gap="var(--space-3)">
              {isInvoiceSendable && !isAuditor && (
                <Button
                  variant={
                    hasUnsavedChanges ? "default-outlined" : "primary-filled"
                  }
                  size="small"
                  onClick={() => {
                    setOpenSendInvoiceConfirmationModal(true);
                  }}
                  disabled={specificLoadingStates.sendingInvoice}
                >
                  {isInvoiceSent ? t("Resend Invoice") : t("Send Invoice")}
                </Button>
              )}

              {isInvoiceSendable && !isAuditor && (
                <Button
                  variant="default-outlined"
                  size="small"
                  onClick={() => {
                    setOpenSendInvoiceAsEstimateModal(true);
                  }}
                  disabled={specificLoadingStates.sendingInvoice}
                >
                  {isEstimate ? t("Resend Estimate") : t("Send Estimate")}
                </Button>
              )}

              <Button
                variant="default-outlined"
                size="small"
                onClick={() => {
                  handleOpenInvoicePreview();
                }}
              >
                {t("Preview Invoice")}
              </Button>

              {isShowRefundButton && !isAuditor && (
                <Button
                  variant="default-outlined"
                  size="small"
                  onClick={() => {
                    setOpenRefundInvoiceModal(true);
                  }}
                >
                  {t("Refund Invoice")}
                </Button>
              )}

              {invoiceData?.status === InvoiceStatus.PAYMENT_FAILED &&
                !isAuditor && (
                  <Button
                    variant="default-outlined"
                    size="small"
                    onClick={() => {
                      setOpenResendFailedNoticeModal(true);
                    }}
                    disabled={specificLoadingStates.resendingFailedNotice}
                  >
                    {t("Re-message")}
                  </Button>
                )}
            </Inline>

            <SectionTabs tabs={sectionTabs} ariaLabel={t("Invoice sections")} />
          </>
        )}
      </Drawer>

      <CommentsPanel
        open={showComments}
        onClose={() => setCommentsOpen(false)}
        drawerWidth={DRAWER_WIDTH}
        docked={commentsDockable}
        commentActions={commentActions}
      />

      {openContactDetailsDrawer && (
        <ContactDrawerRouter
          open={!!openContactDetailsDrawer}
          drawerType={ContactRouterDrawerType.ExistingContact}
          contactId={openContactDetailsDrawer}
          onClose={handleCloseViewContactDetailsDrawer}
        />
      )}

      {openInvoicePreview && (
        <InvoicePreviewContainer
          open={openInvoicePreview}
          invoice={invoice}
          onExit={() => setOpenInvoicePreview(false)}
        />
      )}

      {transactionReviewDrawerId && (
        <TransactionDetailsDrawer
          open={!!transactionReviewDrawerId}
          handleCloseDrawer={() => setTransactionReviewDrawerId(null)}
          transactionId={transactionReviewDrawerId}
        />
      )}

      <EditInvoiceConfirmationModal
        openEditSentInvoiceModal={openEditSentInvoiceModal}
        selectedInvoiceId={invoice?.id}
        onCloseModal={() => setOpenEditSentInvoiceModal(false)}
      />

      <SendInvoiceConfirmationModal
        openSendInvoiceConfirmationModal={openSendInvoiceConfirmationModal}
        invoice={invoice}
        onCloseModal={(sent) => {
          setOpenSendInvoiceConfirmationModal(false);
          if (sent) {
            refetchInvoice();
          }
        }}
      />
      <SendInvoiceAsEstimateConfirmationModal
        openSendInvoiceAsEstimateModal={openSendInvoiceAsEstimateModal}
        invoice={invoice}
        onCloseModal={(sent) => {
          setOpenSendInvoiceAsEstimateModal(false);
          if (sent) {
            refetchInvoice();
          }
        }}
      />
      {isShowRefundButton && (
        <RefundInvoiceConfirmationModal
          openRefundInvoiceModal={openRefundInvoiceModal}
          invoice={invoiceData}
          onCloseModal={(isRefresh) => {
            setOpenRefundInvoiceModal(false);
            if (isRefresh) {
              refetchInvoice();
            }
          }}
        />
      )}

      <ConfirmDialog
        open={openUnsavedChangesModal}
        tone="primary"
        title={t("Unsaved Changes")}
        message={t("UnsavedChangesDescription")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          setOpenUnsavedChangesModal(false);
          handleCloseWithCheck(true);
        }}
        onCancel={() => setOpenUnsavedChangesModal(false)}
      />

      <ResendFailedPaymentNoticeModal
        open={openResendFailedNoticeModal}
        invoice={invoice}
        onCloseModal={(sent) => {
          setOpenResendFailedNoticeModal(false);
          if (sent) {
            refetchInvoice();
          }
        }}
      />
    </>
  );
};

export default InvoiceDetailsDrawer;
